import { Component, OnInit, OnDestroy } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
    TCI_UPDATE_ACTION_TYPE,
    TCI_COMPONENT_MODE,
    TCITenantSelectorType,
    ITCIApplicationUpdateRequest,
    TCIApplicationEndpointLabel,
    ITCIApplicationUpdateResponse,
    TCI_APP_DEPLOYMENT_STAGE
} from "@tci/common";
import { BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput } from "../../ng-app-details/ng-app-details-menu";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { AbstractTCITenantComponent } from "../../ng-tenant/tci-tenant-component";
import { ITCIAppDetailsMenuOutput } from "../../ng-tenant/tci-tenant-components";
import { BaseAppDetailSetEndpointService } from "./base-app-hm-set-endpoint.service";
import { Subscription } from "rxjs";

interface IModalContent {
    TITLE: string;
    DISCLAIMER: string;
    CANCEL_BUTTON: string;
    SUBMIT_BUTTON: string;
    WARNING: string;
}

@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_SET_ENDPOINT.toString(),
    templateUrl: "./base-app-hm-set-endpoint.component.html",
    styleUrls: ["./base-app-hm-set-endpoint.component.less"]
})
export class BaseAppDetailSetEndpointComponent<E, I extends BaseAppDetailMenuComponentInput,
    O extends BaseAppDetailMenuComponentOutput<ITCIAppDetailsMenuOutput>>
    extends AbstractTCITenantComponent<E, I, O>
    implements OnInit, OnDestroy {
    isHM = false; // To toggle mode
    public = false;
    endpointLabel: string;
    routerLink: string[];
    subscriptions: Subscription[] = [];
    isLive = false;

    private modalContent: IModalContent;

    constructor(
        private setEndpointService: BaseAppDetailSetEndpointService,
        private translate: TranslateService,
        private toastr: NgToastrService) {
        super();

        this.subscriptions.push(
            this.setEndpointService.getStatusSubject()
                .filter(status => status.appId === this.input.application.id)
                .map(status => status.status)
                .subscribe(status => {
                    this.public = status === "public";
                    this.setEndpointLabel(this.public);
                })
        );
    }


    ngOnInit(): void {
        this.public = this.input.application.endpointVisibility === "public";
        this.isLive = this.input.application.publishStage === TCI_APP_DEPLOYMENT_STAGE.PUBLISHED;
        this.setEndpointLabel(this.public);
        this.isHM = this.input.mode === TCI_COMPONENT_MODE.COMP_MODE_APP_DETAIL_HAMBURGER_MENU;
        if (this.isHM) {
            const inputObj = this.input as BaseAppDetailMenuComponentInput;
            const isPrivate = inputObj.application.endpointVisibility === "private";
            let disclaimer = this.translate.instant("TCI_HM_SETENDPOINT.SET_PRIVATE_DISCLAIMER");
            if (isPrivate) {
                disclaimer = this.translate.instant("TCI_HM_SETENDPOINT.SET_PUBLIC_DISCLAIMER");
            }

            this.modalContent = {
                TITLE: this.isLive ? this.translate.instant("TCI_HM_SETENDPOINT.TITLE_FOR_LIVE_STATUS")
                    : (isPrivate ? this.translate.instant("TCI_HM_SETENDPOINT.TITLE_TO_PUBLIC")
                        : this.translate.instant("TCI_HM_SETENDPOINT.TITLE_TO_PRIVATE")),
                DISCLAIMER: disclaimer,
                WARNING: this.translate.instant("TCI_HM_SETENDPOINT.WARNING"),
                CANCEL_BUTTON: "Cancel",
                SUBMIT_BUTTON: "Update"
            };
        } else if (this.input.componentOption) {
            this.routerLink = ["../../../details", this.input.componentOption.getRouteId(), this.input.application.id];
        }
    }

    ngOnDestroy(): void {
        this.subscriptions
            .filter(s => !s.closed)
            .forEach(s => s.unsubscribe());
    }

    setEndpointLabel(isPublic: boolean) {
        this.endpointLabel = isPublic ? TCIApplicationEndpointLabel.PUBLIC.getString() : TCIApplicationEndpointLabel.CLOUD_MESH.getString();
    }

    onSubmit(): void {
        const inputObj = this.input as BaseAppDetailMenuComponentInput;
        const setTo = inputObj.application.endpointVisibility === "public" ? "private" : "public";
        const appReq: ITCIApplicationUpdateRequest = {
            appId: inputObj.application.id,
            application: this.input.application,
            actionType: TCI_UPDATE_ACTION_TYPE.APP_ENDPOINT_VISIBILITY,
            tenantType: inputObj.tenantAppType,
            endpointVisibility: setTo
        };

        this.setEndpointService.updateAppEndpointVisibility(appReq).subscribe(
            (rsp: ITCIApplicationUpdateResponse) => {
                // relfect UI model value (?? make call to DS and then set the value)
                inputObj.application.endpointVisibility = setTo;

                this.setEndpointService.publishStatus(inputObj.application.id, setTo);
                this.toastr.clear();
                this.toastr.success(this.input.application.name, rsp.msg);
                this.closeModal(true);
            },
            (err) => {
                this.toastr.clear();
                this.toastr.error(this.input.application.name, err.json().msg);
                this.closeModal();
            }
        );
    }

    onCancel(): void {
        this.closeModal();
    }

    protected closeModal(refresh = false): ITCIAppDetailsMenuOutput {
        const e: ITCIAppDetailsMenuOutput = {
            menuItemSelector: this.input.componentSelector,
            closeModal: true,
            refresh
        };
        this.output.emit(e);
        return e;
    }
}
